"use client";

import classNames from "classnames";
import { useTranslations } from "next-intl";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import SwiperCore from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import { Mousewheel, Pagination } from "swiper/modules";

import useMediaQuery from "@/hooks/useMediaQuery";

import Editorial2Col from "../EditorialDualColumn/EditorialDualColumn";
import HistoryBgImage from "./HistoryBgImage";
import "./HistoryCarousel.css";
import HistoryDesktop from "./HistoryDesktop";
import HistoryMobile from "./HistoryMobile";
import { HistoryModal } from "./HistoryModal";
import { HistoryPaginationBullets } from "./HistoryPaginationBullet";
import { History, HistoryCard } from "./types";

SwiperCore.use([Pagination, Mousewheel]);

const HistoryCarousel: FC<{ entry: History; firstComponent: boolean }> = ({ entry, firstComponent }) => {
  const [swiperInstance, setSwiperInstance] = useState<SwiperCore>();
  const t = useTranslations();
  const [currentCard, setCurrentCard] = useState<HistoryCard>(entry?.years?.[0]?.cards?.[0]);
  const [currentYearGroupIndex, setCurrentYearGroupIndex] = useState(0);
  const [currentBulletIndex, setCurrentBulletIndex] = useState<number | undefined>(0);
  const [openModalCard, setOpenModalCard] = useState<{ card: HistoryCard; year?: number; cardIndex: number }>();
  const paginationRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const paginationActiveBorderRef = useRef<HTMLDivElement>(null);
  const mainContainerRef = useRef<HTMLDivElement>(null);

  const orderedFirstSlideIndex = useMemo(() => {
    const arr: { title: string; year?: number; firstIndex: number; bulletIcon?: any }[] = [];
    let cardSum = 0;
    entry.years.forEach((year, index) => {
      const firstIndex: number = index > 0 ? cardSum : 0;
      cardSum += year.cards?.length;
      let bulletIcon;
      for (let i = 0; i < year.cards?.length; i++) {
        if (year.cards[i].icon) {
          bulletIcon = year.cards[i].icon?.link.icon;
          break;
        }
      }
      arr.push({ title: year.title, year: year.year, firstIndex: firstIndex, bulletIcon: bulletIcon });
    });
    return arr;
  }, [entry?.years]);

  const cardsList = useMemo(() => {
    const cards: {
      yearGroupIndex: number;
      card: HistoryCard;
    }[] = [];
    entry.years.forEach((item, index) => {
      item.cards?.forEach((card) => {
        cards.push({ yearGroupIndex: index, card: card });
      });
    });
    return cards;
  }, [entry.years]);

  return (
    <>
      <div
        className={classNames(
          entry.years.length >= 15 ? "time-history-long" : "time-history",
          "time-history w-full md:w-full relative flex flex-col md:flex-row justify-end  h-screen md:h-auto gap-x-[30px]",
        )}
        ref={mainContainerRef}
      >
        <HistoryBgImage currentCard={currentCard} currentYearGroupIndex={currentYearGroupIndex} />
        <HistoryMobile
          data={entry}
          cardsList={cardsList}
          carouselRef={carouselRef}
          containerRef={mainContainerRef}
          orderedFirstSlideIndex={orderedFirstSlideIndex}
          currentBulletIndex={currentBulletIndex}
          swiperInstance={swiperInstance}
          setSwiperInstance={setSwiperInstance}
          setCurrentBulletIndex={setCurrentBulletIndex}
          setCurrentYearGroupIndex={setCurrentYearGroupIndex}
          setCurrentCard={setCurrentCard}
          setOpenModalCard={setOpenModalCard}
        />
        <HistoryDesktop
          data={entry}
          cardsList={cardsList}
          carouselRef={carouselRef}
          setCurrentYearGroupIndex={setCurrentYearGroupIndex}
          setCurrentBulletIndex={setCurrentBulletIndex}
          setCurrentCard={setCurrentCard}
          setOpenModalCard={setOpenModalCard}
        />
        <HistoryPaginationBullets
          bulletType={entry.variant}
          orderedFirstSlideIndex={orderedFirstSlideIndex}
          currentYearGroupIndex={currentYearGroupIndex}
          paginationActiveBorderRef={paginationActiveBorderRef}
          paginationRef={paginationRef}
          swiperInstance={swiperInstance}
          setCurrentBulletIndex={setCurrentBulletIndex}
          carouselRef={carouselRef}
          containerRef={mainContainerRef}
          firstComponent={firstComponent}
        />
        <div className="swiper-pagination-progress-wrapper px-m relative md:hidden h-xl w-full bg-white flex items-center order-3">
          <div className="pagination-bottom relative w-full bg-gray-2 h-1"></div>
        </div>
      </div>
      {openModalCard?.card?.modal && (
        <HistoryModal
          modal={openModalCard.card.modal}
          setModal={setOpenModalCard}
          year={openModalCard.year}
          image={openModalCard.card.modal.image || openModalCard.card.backgroundImage}
          title={openModalCard.card.title}
          text={openModalCard.card.modal.text || openModalCard.card.description}
          variant={entry.variant}
        />
      )}
    </>
  );
};
export default HistoryCarousel;
